import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { BadgeCheck, CalendarDays, CheckCircle2, Loader2, MapPin, ShieldCheck, UserRound } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import { CATEGORY_IMAGES, MODEL_META } from "@/constants/site";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "fr_customer_token";
const EMPTY = { first_name: "", last_name: "", email: "", phone: "", birthdate: "", license_number: "", notes: "" };

const FLEET_META = {
  CITY: { title: "Fiat Panda Hybrid" },
  COMPACT: { title: "Audi A1 / Renault Clio" },
  "SUV / CROSSOVER": { title: "Opel Crossland" },
  SPORT: { title: "Fiat 500X Sport" },
  PREMIUM: { title: "Mercedes Classe A / B" },
};

const Field = ({ label, ...props }) => (
  <label className="block">
    <span className="text-[10px] font-bold tracking-wider text-gray-400 uppercase">{label}</span>
    <input
      {...props}
      className="mt-1 w-full h-10 rounded-lg border border-gray-200 bg-gray-50 px-3 text-sm outline-none focus:border-fr-navy focus:bg-white transition-colors"
    />
  </label>
);

export const BookingFlow = ({ open, category, model, search, days, onClose }) => {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [account, setAccount] = useState(null);
  // COMANDO SITO FASE 2: coperture/extra dal listino del gestionale; consensi facoltativi NON preselezionati
  const [coverages, setCoverages] = useState([
    { code: "basic", name: "Basic", daily_price: 0, deductible_eur: 800, deposit_eur: 300 },
    { code: "kasko", name: "Kasko", daily_price: 10, deductible_eur: 300, deposit_eur: 300 },
    { code: "full_kasko", name: "Full Kasko", daily_price: 18, deductible_eur: 0, deposit_eur: 200 },
  ]);
  const [extras, setExtras] = useState([]);
  const [coverage, setCoverage] = useState("basic");
  const [selExtras, setSelExtras] = useState([]);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [consents, setConsents] = useState({ marketing_offers: false, review: false, selfie: false });
  const idemRef = useRef("");
  const meta = category ? FLEET_META[category] : null;
  const modelTitle = model && MODEL_META[model] ? model : meta?.title;
  const modelImg = model && MODEL_META[model] ? CATEGORY_IMAGES[category] : CATEGORY_IMAGES[category];

  useEffect(() => {
    if (open) {
      setStep(1);
      setResult(null);
      const token = localStorage.getItem(TOKEN_KEY);
      if (token) {
        axios
          .get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
          .then(({ data }) => {
            setAccount(data);
            setForm((f) => ({
              ...f,
              first_name: f.first_name || data.first_name || "",
              last_name: f.last_name || data.last_name || "",
              email: f.email || data.email || "",
              phone: f.phone || data.phone || "",
            }));
          })
          .catch(() => setAccount(null));
      }
    }
  }, [open, category]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  // COMANDO SITO FASE 2: listino coperture/extra dal gestionale (fallback: valori predefiniti)
  useEffect(() => {
    axios.get(`${API}/fleet`).then(({ data }) => {
      if (Array.isArray(data.coverages) && data.coverages.length) setCoverages(data.coverages);
      if (Array.isArray(data.extras) && data.extras.length) setExtras(data.extras);
    }).catch(() => {});
  }, []);

  const valid =
    form.first_name.length >= 2 &&
    form.last_name.length >= 2 &&
    /^\S+@\S+\.\S+$/.test(form.email) &&
    form.phone.length >= 6 &&
    form.birthdate &&
    form.license_number.length >= 4 &&
    Number(form.driver_age || 99) >= 21 &&
    Number(form.license_years || 99) >= 2 &&
    acceptTerms;

  const submit = async () => {
    if (!valid) {
      toast.error("Compila i campi obbligatori: età minima 21 anni, patente da 2 anni, condizioni e privacy accettate");
      return;
    }
    if (!idemRef.current) idemRef.current = "web-" + Date.now() + "-" + Math.random().toString(36).slice(2, 10);
    setLoading(true);
    try {
      const headers = {};
      const token = localStorage.getItem(TOKEN_KEY);
      if (token) headers.Authorization = `Bearer ${token}`;
      const { data } = await axios.post(
        `${API}/bookings`,
        {
          category,
          preferred_model: model || "",
          location_code: search.location.code,
          pickup_at: search.pickup.toISOString(),
          dropoff_at: search.dropoff.toISOString(),
          customer: form,
          driver_age: form.driver_age ? Number(form.driver_age) : undefined,
          license_years: form.license_years ? Number(form.license_years) : undefined,
          coverage,
          extras: selExtras,
          consents,
          accept_terms: acceptTerms,
          idem_key: idemRef.current,
          channel: "sito",
        },
        { headers }
      );
      setResult(data);
      setStep(3);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore invio prenotazione");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-[calc(100%-24px)] sm:max-w-md rounded-2xl p-0 overflow-hidden max-h-[85vh] overflow-y-auto" data-testid="booking-dialog">
        <div className="bg-fr-navy text-white px-5 py-4">
          <DialogTitle className="font-extrabold tracking-tight text-base" data-testid="booking-title">
            {step === 3 ? "Richiesta inviata!" : "Prenota la tua auto"}
          </DialogTitle>
          <div className="mt-2 flex gap-1.5" data-testid="booking-steps">
            {[1, 2, 3].map((s) => (
              <span key={s} className={`h-1 flex-1 rounded-full transition-colors ${s <= step ? "bg-fr-red" : "bg-white/20"}`} />
            ))}
          </div>
        </div>

        <div className="p-5">
          {step === 1 && meta && (
            <div data-testid="booking-step-summary">
              <div className="flex gap-3 items-center">
                {modelImg ? (
                  <img src={modelImg} alt={modelTitle} className="h-16 w-24 object-contain rounded-lg bg-gray-50 border border-gray-100" />
                ) : (
                  <div className="h-16 w-24 rounded-lg bg-fr-ink flex items-center justify-center text-[9px] text-gray-400 text-center px-2">Foto da gestionale</div>
                )}
                <div>
                  <p className="font-bold text-fr-ink text-sm">{modelTitle}</p>
                  <p className="text-[10px] text-gray-400">o similare • categoria {category}</p>
                </div>
              </div>
              {model && (
                <p className="mt-2 text-[10px] text-gray-500 font-medium leading-relaxed">{MODEL_META[model]?.desc}</p>
              )}

              <div className="mt-4 space-y-2 rounded-xl bg-gray-50 border border-gray-100 p-3 text-xs" data-testid="booking-summary-rows">
                <p className="flex items-center gap-2 text-gray-700">
                  <MapPin size={13} className="text-fr-red" /> {search.location.name}
                </p>
                <p className="flex items-center gap-2 text-gray-700">
                  <CalendarDays size={13} className="text-fr-navy" /> Ritiro {format(search.pickup, "d MMM HH:mm", { locale: it })} → Consegna{" "}
                  {format(search.dropoff, "d MMM HH:mm", { locale: it })}
                </p>
                <p className="flex items-center gap-2 text-gray-700">
                  <ShieldCheck size={13} className="text-fr-navy" /> {days} giorni • Km illimitati inclusi
                </p>
                <p className="flex items-center gap-2 font-bold text-fr-navy">
                  <BadgeCheck size={13} className="text-fr-red" /> Prezzo: DA GESTIONALE — nessuna carta di credito richiesta
                </p>
                {account && (
                  <p className="flex items-center gap-2 font-bold text-fr-navy">
                    <UserRound size={13} className="text-fr-red" /> Prenoti come {account.first_name} {account.last_name} — finirà nella tua area clienti
                  </p>
                )}
              </div>

              <button
                onClick={() => setStep(2)}
                className="mt-4 w-full rounded-xl bg-fr-ink text-white font-bold text-sm py-3 hover:bg-black transition-colors"
                data-testid="booking-continue-button"
              >
                Continua → dati cliente
              </button>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-3" data-testid="booking-step-form">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Nome *" value={form.first_name} onChange={set("first_name")} placeholder="Mario" data-testid="booking-input-first-name" />
                <Field label="Cognome *" value={form.last_name} onChange={set("last_name")} placeholder="Rossi" data-testid="booking-input-last-name" />
              </div>
              <Field label="Email *" type="email" value={form.email} onChange={set("email")} placeholder="mario.rossi@email.it" data-testid="booking-input-email" />
              <Field label="Telefono *" type="tel" value={form.phone} onChange={set("phone")} placeholder="350 000 0000" data-testid="booking-input-phone" />
              <div className="grid grid-cols-2 gap-3">
                <Field label="Data di nascita *" type="date" value={form.birthdate} onChange={set("birthdate")} data-testid="booking-input-birthdate" />
                <Field label="Numero patente *" value={form.license_number} onChange={set("license_number")} placeholder="NA00000AA" data-testid="booking-input-license" />
                <Field label="Età del conducente (min 21)" type="number" min="21" max="99" value={form.driver_age} onChange={set("driver_age")} placeholder="Opzionale" data-testid="booking-input-driver-age" />
                <Field label="Anni di patente (min 2)" type="number" min="2" max="70" value={form.license_years} onChange={set("license_years")} placeholder="Opzionale" data-testid="booking-input-license-years" />
              </div>
              <Field label="Note (volo, treno, hotel…)" value={form.notes} onChange={set("notes")} placeholder="Opzionale" data-testid="booking-input-notes" />

              {/* COMANDO SITO FASE 2.1: coperture Basic/Kasko/Full Kasko (listino dal gestionale) */}
              <div className="rounded-xl border border-gray-100 bg-gray-50 p-3" data-testid="booking-coverage">
                <p className="text-[10px] font-bold tracking-wider text-gray-400 uppercase">Copertura</p>
                {coverages.map((c) => (
                  <label key={c.code} className="mt-1.5 flex items-center gap-2 text-xs text-gray-700 cursor-pointer">
                    <input type="radio" name="coverage" checked={coverage === c.code} onChange={() => setCoverage(c.code)} className="accent-fr-navy" data-testid={`booking-coverage-${c.code}`} />
                    <b>{c.name}</b>
                    <span className="text-[10px] text-gray-500">tetto danni €{c.deductible_eur} • cauzione €{c.deposit_eur}{c.daily_price ? ` • +€${c.daily_price}/giorno` : " • inclusa"}</span>
                  </label>
                ))}
              </div>

              {/* Extra dal listino del gestionale (consegna aeroporto Capodichino gratuita, domicilio gratis entro 5 km) */}
              {(extras.length > 0) && (
                <div className="rounded-xl border border-gray-100 bg-gray-50 p-3" data-testid="booking-extras">
                  <p className="text-[10px] font-bold tracking-wider text-gray-400 uppercase">Extra</p>
                  {extras.map((x) => (
                    <label key={x.code || x.name} className="mt-1.5 flex items-center gap-2 text-xs text-gray-700 cursor-pointer">
                      <input type="checkbox" checked={selExtras.includes(x.code || x.name)} onChange={(e) => setSelExtras(e.target.checked ? [...selExtras, x.code || x.name] : selExtras.filter((y) => y !== (x.code || x.name)))} className="accent-fr-navy" data-testid={`booking-extra-${x.code || x.name}`} />
                      <b>{x.name}</b>
                      {x.daily_price != null && <span className="text-[10px] text-gray-500">€{x.daily_price}/giorno</span>}
                    </label>
                  ))}
                </div>
              )}

              {/* Consensi: condizioni+privacy OBBLIGATORI, 3 facoltativi NON preselezionati */}
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 space-y-1.5" data-testid="booking-consents">
                <label className="flex items-start gap-2 text-xs text-gray-800 cursor-pointer">
                  <input type="checkbox" checked={acceptTerms} onChange={(e) => setAcceptTerms(e.target.checked)} className="mt-0.5 accent-fr-red" data-testid="booking-accept-terms" />
                  <span>Accetto le <a className="underline" href="/legal" target="_blank" rel="noreferrer">Condizioni di noleggio</a> e l'<a className="underline" href="/legal" target="_blank" rel="noreferrer">informativa privacy</a> *</span>
                </label>
                {[
                  ["marketing_offers", "Voglio ricevere offerte promozionali (facoltativo)"],
                  ["review", "Accetto di lasciare una recensione dopo il noleggio (facoltativo)"],
                  ["selfie", "Acconsento a foto/contenuti social del viaggio (facoltativo)"],
                ].map(([k, label]) => (
                  <label key={k} className="flex items-start gap-2 text-xs text-gray-600 cursor-pointer">
                    <input type="checkbox" checked={consents[k]} onChange={(e) => setConsents({ ...consents, [k]: e.target.checked })} className="mt-0.5 accent-fr-navy" data-testid={`booking-consent-${k}`} />
                    <span>{label}</span>
                  </label>
                ))}
              </div>
              <p className="text-[10px] text-gray-500 bg-gray-50 border border-gray-100 rounded-lg p-2" data-testid="booking-request-note">
                La richiesta <b>non è una conferma</b>: prezzo e disponibilità vengono confermati dal personale (obiettivo 30 min in orario d'ufficio). Nessuna carta di credito richiesta.
              </p>
              <button
                onClick={submit}
                disabled={loading}
                className="w-full rounded-xl bg-fr-red text-white font-bold text-sm py-3 hover:bg-red-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
                data-testid="booking-submit-button"
              >
                {loading ? <Loader2 size={16} className="animate-spin" /> : null}
                {loading ? "Invio in corso…" : "Prenota ora — conferma da gestionale"}
              </button>
            </div>
          )}

          {step === 3 && result && (
            <div className="text-center" data-testid="booking-step-success">
              <CheckCircle2 size={44} className="mx-auto text-green-500" />
              <p className="mt-2 font-mono font-extrabold text-xl tracking-wider text-fr-navy" data-testid="booking-reference">
                {result.reference}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {result.preferred_model || result.category} • {result.location_name} • {result.days} giorni
              </p>
              {/* COMANDO SITO FASE 2.2/2.4 */}
              {result.status === "presa_in_carico" ? (
                <div className="mt-3 rounded-xl bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900" data-testid="booking-pending-note">
                  <b>Richiesta presa in carico.</b> Il gestionale è momentaneamente irraggiungibile: la richiesta è in coda e verrà inviata automaticamente, senza duplicazioni.
                </div>
              ) : (
                result.payment_url && (
                  <div className="mt-3 rounded-xl bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900" data-testid="booking-payment-note">
                    <b>Hai 30 minuti per completare il pagamento.</b>
                    <a href={result.payment_url} target="_blank" rel="noreferrer" className="mt-2 block rounded-lg bg-fr-navy text-white font-bold px-4 py-2" data-testid="booking-payment-link">
                      Completa il pagamento ora
                    </a>
                  </div>
                )
              )}
              <div className="mt-4 text-left rounded-xl bg-gray-50 border border-gray-100 p-3 text-[11px] text-gray-600 space-y-1.5">
                <p>• Ti contattiamo entro 30 minuti (orario ufficio) per confermare prezzo e disponibilità dal gestionale.</p>
                <p>• Consegna gratuita da 5 a 90 minuti in base alla distanza: aeroporto, stazioni, hotel, B&B e casa.</p>
                <p>• Nessun pagamento ora: nessuna carta di credito richiesta.</p>
                <p data-testid="booking-whatsapp-status">
                  {result.whatsapp_status === "sent"
                    ? "• Conferma inviata via WhatsApp al tuo numero."
                    : "• Riceverai la conferma dal gestionale via WhatsApp o telefono."}
                </p>
                {!account && (
                  <p>
                    • <a href="/account" className="font-bold text-fr-navy underline">Crea l'account</a> per ritrovare prenotazioni, dati salvati e offerte.
                  </p>
                )}
              </div>
              <button onClick={onClose} className="mt-4 w-full rounded-xl bg-fr-navy text-white font-bold text-sm py-3 hover:bg-fr-navydark transition-colors" data-testid="booking-done-button">
                Fatto
              </button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BookingFlow;
